import React, { useState, useEffect, useCallback } from 'react';
import { Alert } from 'antd';
import { message } from 'antd';
import RoundedModal from '@components/roundedModal/roundedModal';
import useTranslation from '@hooks/useTranslation';
import useInvokeHubFunction from '@common/hooks/useInvokeHubFunction';
import useKemuHubLink from '@common/hooks/useHubLink';
import organizationApi from '@src/api/organization/organizationApi';
import { KemuHubFunctions, ExchangeSessionTokenForApiKeyArgs, ExchangeSessionTokenForApiKeyResponse } from '@kemu-io/hs-types';

type Props = {
  visible: boolean;
  onClose: () => void;
  onDismiss: () => void;
};

/**
 * Modal component for configuring API key for the hub
 */
const HubApiKeyModal: React.FC<Props> = ({ visible, onClose, onDismiss }) => {
  const t = useTranslation('HubApiKeyModal');
  const tCommon = t.withBaseKey('CommonWords');
  const invokeHubFunction = useInvokeHubFunction();
  const { status } = useKemuHubLink();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const [hasSeenDisconnect, setHasSeenDisconnect] = useState(false);

  // Monitor hub reconnection after restart
  useEffect(() => {
    if (isRestarting) {
      // Track when hub disconnects after restart
      if (status === 'disconnected' && !hasSeenDisconnect) {
        setHasSeenDisconnect(true);
      }
      
      // Once we've seen a disconnect and hub is back to acknowledged, we're done
      if (hasSeenDisconnect && status === 'acknowledged') {
        setIsRestarting(false);
        setHasSeenDisconnect(false);
        message.success(t('Success.ApiKeyConfigured'));
        onClose();
      }
    }
  }, [isRestarting, hasSeenDisconnect, status, t, onClose]);

  /**
   * Handles the auto-generate API key flow
   */
  const handleAutoGenerate = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Get session token from API
      const { sessionToken } = await organizationApi.autogenApiKey();

      // Step 2: Exchange session token with hub
      const exchangeResponse = await invokeHubFunction<[ExchangeSessionTokenForApiKeyResponse]>(
        KemuHubFunctions.ExchangeSessionTokenForApiKey,
        [{ sessionToken }] as ExchangeSessionTokenForApiKeyArgs
      );

      const { success, error } = exchangeResponse?.[0] || {};
      if (!success) {
        throw new Error(error || t('Error.ExchangeFailed'));
      }

      // Step 3: Restart the hub app
      setLoading(false);
      setIsRestarting(true);
      setHasSeenDisconnect(false);
      
      await invokeHubFunction(KemuHubFunctions.RestartApp, []);
      
      // The effect hook will handle waiting for hub to come back online
    } catch (err) {
      setLoading(false);
      setIsRestarting(false);
      const errorMessage = err instanceof Error ? err.message : t('Error.GenerateFailed');
      setError(errorMessage);
      message.error(errorMessage);
    }
  }, [invokeHubFunction, status, t]);

  /**
   * Handles modal cancel/dismiss
   */
  const handleCancel = useCallback(() => {
    if (!loading || isRestarting) {
      onDismiss();
      onClose();
    }
  }, [loading, isRestarting, onDismiss, onClose]);

  /**
   * Handles auto-generate action (OK button)
   */
  const handleOk = useCallback(() => {
    if (!loading && !isRestarting) {
      handleAutoGenerate();
    }
  }, [loading, isRestarting, handleAutoGenerate]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setLoading(false);
      setError(null);
      setIsRestarting(false);
      setHasSeenDisconnect(false);
    }
  }, [visible]);

  return (
    <RoundedModal
      visible={visible}
      onCancel={handleCancel}
      onOk={isRestarting ? undefined : handleOk}
      title={t('Title')}
      width={600}
      // Allow closing the modal while restarting, just in case of errors
      closable={!loading || isRestarting}
      closeOnMaskClick={false}
      loading={loading || isRestarting}
      cancelBtnLabel={isRestarting ? undefined : t('DismissButton')}
      okBtnLabel={isRestarting ? undefined : (loading ? tCommon('Loading') : t('AutoGenerateButton'))}
      disableOkButton={loading || isRestarting}
      disableCancelButton={loading || isRestarting}
      loadingOkBtn={loading}
    >
      <div>
        {error && (
          <Alert
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        {isRestarting ? (
          <div style={{ marginBottom: 40, width: '100%', textAlign: 'center' }}>
            <p>{t('RestartingMessage')}</p>
          </div>
        ) : (
          <div>
            <p style={{ marginBottom: 24 }}>{t('Message')}</p>
          </div>
        )}
      </div>
    </RoundedModal>
  );
};

export default HubApiKeyModal;

